import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { usePlan } from '@/hooks/usePlan'
import { Modal, ModalType } from '@/components/Modal'

interface ModalState {
  isOpen: boolean
  title: string
  message: string
  type: ModalType
  onConfirm?: () => void
  confirmText?: string
}

export const LeadDetail: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [newStatus, setNewStatus] = useState('')
  const { hasFeature } = usePlan()
  const canGenerateProposal = hasFeature('proposals')

  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  })

  const showModal = (title: string, message: string, type: ModalType = 'info', onConfirm?: () => void, confirmText?: string) => {
    setModal({ isOpen: true, title, message, type, onConfirm, confirmText })
  }

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }))
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['lead', id],
    queryFn: async () => {
      const response = await api.get(`/leads/${id}`)
      return response.data.data
    },
  })

  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      await api.put(`/leads/${id}`, { status })
    },
    onSuccess: () => {
      refetch()
      setNewStatus('')
    },
    onError: (error: any) => {
      showModal(t('common.error'), error?.response?.data?.error || 'Failed to update status', 'error')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/leads/${id}`)
    },
    onSuccess: () => {
      closeModal()
      navigate('/leads')
    },
    onError: (error: any) => {
      showModal(t('common.error'), error?.response?.data?.error || 'Failed to delete lead', 'error')
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">{t('common.noResults')}</div>
      </div>
    )
  }

  const lead = data
  const statusOptions = ['new', 'contacted', 'qualified', 'converted', 'lost']

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-green-500/20 text-green-400'
      case 'contacted': return 'bg-blue-500/20 text-blue-400'
      case 'qualified': return 'bg-purple-500/20 text-purple-400'
      case 'converted': return 'bg-teal-500/20 text-teal-400'
      case 'lost': return 'bg-red-500/20 text-red-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  const getStatusLabel = (status: string) => {
    const translated = t(`leads.${status}`)
    return translated !== `leads.${status}` ? translated : status.replace('_', ' ')
  }

  return (
    <div>
      {/* Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={modal.onConfirm}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={t('common.cancel')}
        isLoading={deleteMutation.isPending}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <div>
          <button
            onClick={() => navigate('/leads')}
            className="text-sm text-gray-400 hover:text-white mb-2 flex items-center gap-1"
          >
            {t('leads.backToLeads')}
          </button>
          <h1 className="text-3xl font-bold">{lead.name}</h1>
          <p className="text-gray-400 mt-1">{lead.email}</p>
        </div>
        <div className="flex gap-2">
          {canGenerateProposal ? (
            <button
              onClick={() => navigate(`/proposals?leadId=${id}`)}
              className="px-4 py-2 bg-[#006cd4]/20 text-[#006cd4] border border-[#006cd4]/20 rounded-lg hover:bg-[#006cd4]/30 transition-colors"
            >
              {t('leads.generateProposal')}
            </button>
          ) : (
            <button
              disabled
              className="px-4 py-2 bg-gray-500/10 text-gray-500 border border-gray-500/20 rounded-lg cursor-not-allowed"
              title={t('leads.proposalUpgrade') || 'Upgrade to Pro to generate proposals'}
            >
              {t('leads.generateProposal')}
            </button>
          )}
          <button
            onClick={() => {
              showModal(
                t('leads.deleteTitle') || 'Delete Lead',
                t('leads.deleteConfirm'),
                'confirm',
                () => deleteMutation.mutate(),
                t('common.delete')
              )
            }}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50"
          >
            {t('common.delete')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4">{t('leads.leadInfo')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('leads.reference')}</label>
                <div className="text-white font-mono">{lead.reference}</div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('common.status')}</label>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                    {getStatusLabel(lead.status)}
                  </span>
                  <select
                    value={newStatus || lead.status}
                    onChange={(e) => {
                      setNewStatus(e.target.value)
                      if (e.target.value !== lead.status) {
                        updateStatusMutation.mutate(e.target.value)
                      }
                    }}
                    className="px-2 py-1 bg-[#1a1a25] border border-white/10 rounded text-sm text-white focus:outline-none focus:border-[#006cd4]"
                  >
                    {statusOptions.map(status => (
                      <option key={status} value={status}>
                        {getStatusLabel(status)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('leads.company')}</label>
                <div className="text-white">{lead.company || '-'}</div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('leads.budget')}</label>
                <div className="text-white">{lead.budget_range || '-'}</div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('leads.timeline')}</label>
                <div className="text-white">{lead.timeline || '-'}</div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('leads.qualityScore')}</label>
                <div className="text-white">{lead.quality_score || '-'}/10</div>
              </div>
            </div>
          </div>

          {/* Project Description */}
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4">{t('leads.projectDescription')}</h2>
            <p className="text-gray-300 whitespace-pre-wrap">{lead.project_description || t('leads.noDescription')}</p>
          </div>

          {/* Chat Summary */}
          {lead.chat_summary && (
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">{t('leads.chatSummary')}</h2>
              <p className="text-gray-300">{lead.chat_summary}</p>
            </div>
          )}

          {/* Dynamic data captured through the tenant's custom lead fields */}
          {lead.dynamicData && Object.keys(lead.dynamicData).length > 0 && (
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">{t('leads.dynamicData')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(lead.dynamicData).map(([key, value]) => (
                  <div key={key}>
                    <label className="block text-sm text-gray-400 mb-1 capitalize">{key.replace(/_/g, ' ')}</label>
                    <div className="text-white whitespace-pre-wrap">{String(value)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Additional Info */}
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-400 mb-4">{t('leads.additionalInfo')}</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-400">{t('leads.objective')}</span>
                <span className="text-white text-sm">{lead.objective || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('leads.existingProject')}</span>
                <span className="text-white text-sm">{lead.existing_project ? t('common.yes') : t('common.no')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('leads.preferredTech')}</span>
                <span className="text-white text-sm">{lead.preferred_tech || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('leads.integrations')}</span>
                <span className="text-white text-sm">{lead.integrations || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('leads.maintenance')}</span>
                <span className="text-white text-sm">{lead.needs_maintenance ? t('common.yes') : t('common.no')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('conversations.source')}</span>
                <span className="text-white text-sm">{lead.source || t('conversations.widget')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('leads.locale')}</span>
                <span className="text-white text-sm">{lead.locale || 'en'}</span>
              </div>
            </div>
          </div>

          {/* Created */}
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-400 mb-4">{t('leads.timelineSidebar')}</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-400">{t('leads.createdAt')}</span>
                <span className="text-white text-sm">{new Date(lead.created_at).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('leads.updatedAt')}</span>
                <span className="text-white text-sm">{new Date(lead.updated_at).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Conversation Link */}
          {lead.conversation_id && (
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h3 className="text-sm font-medium text-gray-400 mb-4">{t('leads.sourceConversation')}</h3>
              <button
                onClick={() => navigate(`/conversations/${lead.conversation_id}`)}
                className="w-full px-4 py-2 bg-[#006cd4]/20 text-[#006cd4] rounded-lg hover:bg-[#006cd4]/30 transition-colors"
              >
                {t('leads.viewConversation')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
